import React, { useState, useEffect, useMemo } from 'react';
import { X, Zap, Check, AlertCircle, Info } from 'react-feather';
import { InlineSelect, InlineCheckbox } from './InlineTable';
import { ToolbarButton } from './ConfigPages';
import { getAiJournalCoaMappingSuggestions } from '../utils/api';

/**
 * Modal for AI-powered journal entry type to COA mapping
 */
const AiJournalCoaMappingModal = ({
  isOpen,
  onClose,
  journalEntryTypes,
  chartAccounts,
  orgId,
  locationId,
  onApplyMappings
}) => {
  const [includeAlreadyMapped, setIncludeAlreadyMapped] = useState(false);
  const [userPrompt, setUserPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [unmappedTypes, setUnmappedTypes] = useState([]);
  const [selectedMappings, setSelectedMappings] = useState(new Set());
  const [metrics, setMetrics] = useState(null);
  const [hasRun, setHasRun] = useState(false);

  const typesToMap = useMemo(() => {
    const active = journalEntryTypes.filter(jt => !jt.isDeleted);
    if (includeAlreadyMapped) return active;
    return active.filter(jt => !jt.chartOfAccountId);
  }, [journalEntryTypes, includeAlreadyMapped]);

  const coaOptions = useMemo(() =>
    chartAccounts.map(ca => ({
      value: ca.id,
      label: `${ca.accountNumber} - ${ca.accountName}`
    })),
    [chartAccounts]
  );

  useEffect(() => {
    if (!isOpen) {
      setSuggestions([]);
      setUnmappedTypes([]);
      setSelectedMappings(new Set());
      setError(null);
      setMetrics(null);
      setHasRun(false);
      setUserPrompt('');
    }
  }, [isOpen]);

  const handleRunAiMapping = async () => {
    if (typesToMap.length === 0) {
      setError('No journal entry types to map. All types may already be mapped.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuggestions([]);
    setUnmappedTypes([]);

    try {
      const result = await getAiJournalCoaMappingSuggestions({
        orgId,
        locationId,
        journalEntryTypes: typesToMap.map(jt => ({
          id: jt.id,
          name: jt.name,
          category: jt.category || '',
          mappedType: jt.mappedType || ''
        })),
        userPrompt: userPrompt.trim() || undefined
      });

      if (result.success) {
        const suggestionsWithState = (result.mappings || []).map(m => ({
          ...m,
          selectedChartOfAccountId: m.chartOfAccountId
        }));
        setSuggestions(suggestionsWithState);
        setUnmappedTypes(result.unmappedTypes || []);
        setMetrics(result.metrics);

        // Auto-select high-confidence mappings (>= 0.7)
        const highConfidence = new Set(
          suggestionsWithState
            .filter(m => m.confidence >= 0.7)
            .map(m => m.journalEntryTypeId)
        );
        setSelectedMappings(highConfidence);
        setHasRun(true);
      } else {
        setError(result.error || 'AI mapping failed. Please try again.');
      }
    } catch (err) {
      console.error('Error running AI journal mapping:', err);
      setError(err.message || 'Failed to get AI suggestions. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMapping = (id) => {
    setSelectedMappings(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedMappings(new Set(suggestions.map(m => m.journalEntryTypeId)));
  const deselectAll = () => setSelectedMappings(new Set());

  const handleCoaChange = (journalEntryTypeId, newCoaId) => {
    setSuggestions(prev =>
      prev.map(m => m.journalEntryTypeId === journalEntryTypeId
        ? { ...m, selectedChartOfAccountId: newCoaId }
        : m
      )
    );
  };

  const handleApplyMappings = () => {
    const mappingsToApply = suggestions
      .filter(m => selectedMappings.has(m.journalEntryTypeId))
      .map(m => ({
        journalEntryTypeId: m.journalEntryTypeId,
        chartOfAccountId: m.selectedChartOfAccountId
      }));
    onApplyMappings(mappingsToApply);
  };

  const confidenceTag = (confidence) => {
    if (confidence >= 0.8) return 'cp-tag cp-ok cp-mono';
    if (confidence >= 0.6) return 'cp-tag cp-warn cp-mono';
    return 'cp-tag cp-mono';
  };

  if (!isOpen) return null;

  return (
    <div className="cp-modal-scrim" role="dialog" aria-modal="true" aria-label="AI map journal types to accounts">
      <div className="cp-modal cp-wide" style={{ display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div className="cp-modal-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span className="cp-btn cp-ai cp-icon" aria-hidden="true" style={{ pointerEvents: 'none' }}><Zap size={16} /></span>
            <div>
              <h2>AI map journal types to accounts</h2>
              <div className="cp-help">Suggests a chart-of-accounts entry for each journal type; you pick which to apply.</div>
            </div>
          </div>
          <ToolbarButton variant="ghost" iconOnly icon={X} aria-label="Close" onClick={onClose} />
        </div>

        {/* Content */}
        <div className="cp-modal-body" style={{ flex: 1, overflowY: 'auto' }}>
          {!hasRun && (
            <div className="cp-field" style={{ gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <InlineCheckbox
                    checked={includeAlreadyMapped}
                    onChange={(e) => setIncludeAlreadyMapped(e.target.checked)}
                  />
                  <span style={{ fontSize: 13, color: 'var(--lodge-onyx-700)' }}>Include already-mapped types (for remapping)</span>
                </label>
                <span className="cp-meta">{typesToMap.length} type{typesToMap.length !== 1 ? 's' : ''} to map</span>
              </div>
              <div className="cp-note">
                <Info size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>
                  The AI reads each journal type&apos;s name and category (revenue, tax, payments, ledger)
                  and suggests the most appropriate GL account based on USALI conventions.
                </span>
              </div>
              <div className="cp-field">
                <label className="cp-label" htmlFor="ai-journal-coa-prompt">
                  Additional instructions <span style={{ fontWeight: 400, color: 'var(--lodge-onyx-400)' }}>(optional)</span>
                </label>
                <textarea
                  id="ai-journal-coa-prompt"
                  className="cp-input"
                  value={userPrompt}
                  onChange={(e) => setUserPrompt(e.target.value)}
                  placeholder="e.g. &quot;Prefer accounts starting with 4 for revenue&quot; or &quot;Map taxes to liability accounts&quot;"
                  rows={3}
                />
              </div>
            </div>
          )}

          {!hasRun && !isLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
              <ToolbarButton variant="primary" icon={Zap} onClick={handleRunAiMapping} disabled={typesToMap.length === 0}>
                Generate suggestions
              </ToolbarButton>
            </div>
          )}

          {isLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 0', gap: 12 }}>
              <div
                aria-hidden="true"
                className="animate-spin"
                style={{
                  width: 40, height: 40, borderRadius: '50%',
                  border: '3px solid var(--lodge-orange-400)', borderTopColor: 'transparent',
                }}
              />
              <div style={{ fontSize: 14, color: 'var(--lodge-onyx-700)' }}>Analyzing journal types and generating suggestions…</div>
              <div style={{ fontSize: 12, color: 'var(--lodge-onyx-400)' }}>This may take a few moments</div>
            </div>
          )}

          {error && (
            <div className="cp-note cp-warn" role="alert" style={{ marginTop: 12 }}>
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{error}</span>
            </div>
          )}

          {hasRun && !isLoading && suggestions.length > 0 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ToolbarButton small variant="ghost" onClick={selectAll}>Select all</ToolbarButton>
                  <ToolbarButton small variant="ghost" onClick={deselectAll}>Deselect all</ToolbarButton>
                  <span className="cp-meta">{selectedMappings.size} selected</span>
                </div>
                <span className="cp-meta">
                  {suggestions.length} suggestion{suggestions.length !== 1 ? 's' : ''}
                  {unmappedTypes.length > 0 && ` · ${unmappedTypes.length} could not be mapped`}
                  {metrics && ` · cost $${metrics.estimatedCost?.toFixed(4) || '0.0000'}`}
                </span>
              </div>

              <div className="it-frame">
                <div className="it-scroll" style={{ '--it-max-h': '48vh' }}>
                  <table className="it-tbl">
                    <thead>
                      <tr>
                        <th style={{ width: 44, textAlign: 'center', padding: 0 }}>Use</th>
                        <th>Journal type</th>
                        <th style={{ minWidth: 300 }}>Suggested account</th>
                        <th style={{ width: 110 }}>Confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suggestions.map((suggestion) => {
                        const isSelected = selectedMappings.has(suggestion.journalEntryTypeId);
                        return (
                          <tr key={suggestion.journalEntryTypeId} className={`cp-row${isSelected ? ' it-row-selected' : ''}`}>
                            <td style={{ textAlign: 'center' }}>
                              <InlineCheckbox
                                checked={isSelected}
                                onChange={() => toggleMapping(suggestion.journalEntryTypeId)}
                              />
                            </td>
                            <td>
                              <div className="cp-cell" style={{ padding: '6px 12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span className="cp-name">{suggestion.journalEntryTypeName}</span>
                                  {suggestion.category && <span className="cp-tag">{suggestion.category}</span>}
                                </div>
                                {suggestion.reasoning && (
                                  <div className="cp-code" style={{ fontFamily: 'var(--font-sans)', whiteSpace: 'normal' }}>{suggestion.reasoning}</div>
                                )}
                              </div>
                            </td>
                            <td>
                              <InlineSelect
                                value={suggestion.selectedChartOfAccountId ?? null}
                                options={coaOptions}
                                onChange={(v) => handleCoaChange(suggestion.journalEntryTypeId, v ?? undefined)}
                                placeholder="Select account…"
                                rowActive
                                minWidth={300}
                                ariaLabel="Chart of account"
                              />
                            </td>
                            <td>
                              <div className="cp-cell">
                                <span className={confidenceTag(suggestion.confidence)}>{Math.round(suggestion.confidence * 100)}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {unmappedTypes.length > 0 && (
                <div className="cp-note cp-warn" style={{ marginTop: 12 }}>
                  <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {unmappedTypes.length} type{unmappedTypes.length !== 1 ? 's' : ''} could not be mapped
                    </div>
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                      {unmappedTypes.map((ut) => (
                        <li key={ut.journalEntryTypeId}><strong>{ut.journalEntryTypeName}</strong>: {ut.reason}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </>
          )}

          {hasRun && !isLoading && suggestions.length === 0 && !error && (
            <div className="cp-empty">
              <h3>No suggestions</h3>
              <p>
                The AI could not match any of these journal types to an account.
                {unmappedTypes.length > 0 && ` ${unmappedTypes.length} type${unmappedTypes.length !== 1 ? 's' : ''} could not be matched.`}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="cp-modal-foot" style={{ justifyContent: 'space-between' }}>
          <ToolbarButton variant="ghost" onClick={onClose}>Cancel</ToolbarButton>
          {hasRun && suggestions.length > 0 && (
            <ToolbarButton variant="primary" icon={Check} onClick={handleApplyMappings} disabled={selectedMappings.size === 0}>
              Apply {selectedMappings.size} mapping{selectedMappings.size !== 1 ? 's' : ''}
            </ToolbarButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default AiJournalCoaMappingModal;
